import { Controller } from "@hotwired/stimulus"
import { drawWaveform, placeholderPeaks } from "../lib/waveform"
import { VoicePlayback } from "../models/voice_playback"
import { formatDuration } from "../helpers/timing_helper"

// Ported from Beep's voice player: Web Audio playback (no lock-screen track on phones), a canvas
// wave drawn from the peaks saved with the recording, and the file fetched only once it is on screen.
export default class extends Controller {
  static targets = [ "button", "canvas", "time" ]
  static values = { waveform: Array, source: String, length: Number }

  #peaks = []
  #seen = null
  #playback = null
  #progressRaf = null
  #drawnBar = -1
  #resize = null

  connect() {
    this.#playback = new VoicePlayback(this.sourceValue, {
      onEnd: () => this.#finished(),
      onPause: () => this.#resting()
    })
    this.#showTime(this.#length)
    this.#peaks = this.#storedPeaks()
    if (this.#peaks.length === 0) this.#peaks = placeholderPeaks()
    this.#render(0)
    this.#warmWhenSeen()
    this.#resize = new ResizeObserver(() => { this.#drawnBar = -1; this.#render(this.#ratio) })
    this.#resize.observe(this.canvasTarget)
  }

  disconnect() {
    this.#seen?.disconnect()
    this.#seen = null
    this.#resize?.disconnect()
    this.#resize = null
    this.#stopProgressLoop()
    this.#playback?.release()
    this.#playback = null
  }

  async toggle() {
    if (this.#playback?.playing) {
      this.#playback.pause()
    } else {
      await this.#start()
    }
  }

  seek(event) {
    const length = this.#length
    if (!length) return

    const box = this.canvasTarget.getBoundingClientRect()
    const ratio = Math.min(Math.max((event.clientX - box.left) / box.width, 0), 1)
    this.#playback?.seek(ratio * length)
    this.#render(ratio)
    this.#showTime(ratio * length)
  }

  // Arrow keys move five seconds, so the wave works without a pointer too.
  seekByKey(event) {
    const step = { ArrowRight: 5, ArrowLeft: -5 }[event.key]
    const length = this.#length
    if (!step || !length || !this.#playback) return

    event.preventDefault()
    const target = Math.min(Math.max(this.#playback.currentTime + step, 0), length)
    this.#playback.seek(target)
    this.#render(target / length)
    this.#showTime(target)
  }

  #warmWhenSeen() {
    if (!this.sourceValue) return

    this.#seen = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return

      this.#playback?.warm()
      this.#seen?.disconnect()
      this.#seen = null
    })
    this.#seen.observe(this.element)
  }

  async #start() {
    this.element.dataset.voicePlayerState = "playing"
    this.buttonTarget.setAttribute("aria-pressed", "true")
    this.buttonTarget.setAttribute("aria-label", "Pausar áudio")

    try {
      await this.#playback.play()
    } catch {
      this.#failed()
      return
    }

    this.#startProgressLoop()
  }

  #finished() {
    this.#stopProgressLoop()
    this.#resting()
    this.#render(0)
    this.#showTime(this.#length)
  }

  #resting() {
    this.#stopProgressLoop()
    this.element.dataset.voicePlayerState = "idle"
    this.buttonTarget.setAttribute("aria-pressed", "false")
    this.buttonTarget.setAttribute("aria-label", "Reproduzir áudio")
  }

  #failed() {
    this.#resting()
    this.element.dataset.voicePlayerState = "failed"
    this.buttonTarget.title = "Não foi possível reproduzir este áudio neste navegador"
    this.timeTarget.textContent = "erro"
  }

  #progress() {
    const length = this.#length
    if (!length) return

    this.#render(this.#playback.currentTime / length)
    this.#showTime(this.#playback.currentTime)
  }

  get #ratio() {
    const length = this.#length
    return length && this.#playback ? this.#playback.currentTime / length : 0
  }

  get #length() {
    return this.#playback?.duration || (this.hasLengthValue ? this.lengthValue : 0)
  }

  #storedPeaks() {
    if (!this.hasWaveformValue || this.waveformValue.length === 0) return []
    return this.waveformValue.map((value) => value / 100)
  }

  #render(progress) {
    const bar = Math.round(this.#peaks.length * Math.min(Math.max(progress, 0), 1))
    if (bar === this.#drawnBar) return

    this.#drawnBar = bar
    drawWaveform(this.canvasTarget, this.#peaks, progress)
    this.canvasTarget.setAttribute("aria-valuenow", String(Math.round(Math.min(Math.max(progress, 0), 1) * 100)))
  }

  #startProgressLoop() {
    this.#stopProgressLoop()
    const tick = () => {
      this.#progress()
      this.#progressRaf = requestAnimationFrame(tick)
    }
    this.#progressRaf = requestAnimationFrame(tick)
  }

  #stopProgressLoop() {
    if (this.#progressRaf) {
      cancelAnimationFrame(this.#progressRaf)
      this.#progressRaf = null
    }
  }

  #showTime(seconds) {
    this.timeTarget.textContent = formatDuration(seconds)
  }
}
